import { THealthDomain } from '@/types/Health.types';

export const APP_NAME = 'MyOmnia';
export const APP_DESCRIPTION = 'Empowering Your Wholeness';

/** Formats the browser tab title as `MyOmnia | {page}`, falling back to just `MyOmnia`. */
export const formatPageTitle = (page?: string) => (page ? `${APP_NAME} | ${page}` : APP_NAME);
export const HARDCODED_APP_VERSION = 'v1.0.0'; // TODO: hardcoded until we decide on app versioning

export const PROD_APP_URL = 'app.myomnia.health';

export const CALENDLY_ROOT_ELEMENT_ID = '__calendly';

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const NAME_REGEX = /^[a-zA-Z'\-áéíóúàèìòùâêîôûäöüÿñçåøčšžňřąęñ ÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄÖÜŸÑÇÅØČŠŽŇŘĄĘÑ ]+$/;

export const PHONE_REGEX = /^(?:\+?\d{1,2}\s?)?(\(?\d{2,3}\)?[\s.-]?)\d{3}[\s-]?\d{3,4}$/;

export const URL_REGEX = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const NONE_OF_THE_ABOVE_LABEL = 'None of the above';
export const MODAL_QUESTION_FORM_KEY = 'modal_form_key';

export const HIGH_CONCERN_LABEL = 'High Concern';
export const SOME_CONCERN_LABEL = 'Some Concern';
export const LOW_CONCERN_LABEL = 'Low Concern';

export const NO_CHANGE_CONCERN_LABEL = 'No change';

export const OTHER_PLEASE_SPECIFY = 'Other (please specify)';

export const RIGHT_ARROW_CHARACTER = '➔';

export const SCREENER_TIME_TO_COMPLETION_MESSAGE = 'Average time to complete the screener: 1min 32sec';

export const INVALID_PUBLIC_SCREENER_SUBMISSION_ID = 'invalid-submission-id';

export const PEER_SUPPORT_LEAD_UPGRADE_NOT_ALLOWED =
  'To set as Peer Support Lead you must first promote member to Peer Support.';

export const GOAL_NEXT_STEP_DESCRIPTION = `Moving Forward in life requires a vision of a realistic, brighter future. As you consider what that includes, write down what your ideal, whole self looks like.`;

// prettier-ignore
export const DOMAIN_NEW_GOAL_PLACEHOLDERS = {
  overall: [ 'Balance body, mind, work, relationships', 'Live with awareness, gratitude, purpose', 'Grow through reflection and connection' ],
  mental: [ 'Challenge unhelpful thoughts with truth', 'Build habits that support clarity', 'Practice lifelong learning and curiosity', ],
  vocational: [ 'Practice a work skill for fifteen minutes', 'Seek growth in your chosen field', 'Find fulfillment beyond just income' ],
  spiritual: [ 'Connect with your inner self', 'Meditate five minutes every morning', 'List five things I’m grateful for' ],
  fun: [ 'Make time for play and laughter', 'Try new hobbies or experiences', 'Share joy with friends and loved ones' ],
  physical: [ 'Move your body with joy each day', 'Fuel yourself with nourishing foods', 'Prioritize rest and deep sleep' ],
  social: [ 'Nurture meaningful, supportive connections', 'Listen with empathy and presence', 'Contribute positively to your community' ],
  purpose: [ 'Find purpose that resonates with you', 'Set goals aligned with your values', 'Live each day with intention', ],
};

export const MAX_IMAGE_SIZE = '3MB';

export const SMART_GOALS_TEXT_SHORT = `SMART framework`;
export const SMART_GOALS_TEXT_LONG = `Use the SMART framework, a proven method for creating achievable goals. SMART is an acronym for goal-setting that stands for Specific, Measurable, Achievable, Relevant, and Time-bound. To set a SMART goal, you must first define it clearly, then establish how you will track your progress, ensure it's a goal you can realistically accomplish with the available resources, verify it aligns with your larger objectives, and finally, set a specific deadline.`;

export const EMPTY_STRING_VALUE = 'N/A';

export const CORTEX_LOADING_TEXT = 'Generating Insights...';

export const DOMAIN_STATUS_INDICATORS = ['STRENGTH', 'AVERAGE', 'DEVELOPMENT_AREA'] as const;

export const LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;

export const NO_ID = 'no-id';

export const LONG_DASH = '—';

export const LOCAL_US_STATES = [
  {
    label: 'Alabama',
    code: 'AL',
  },
  {
    label: 'Alaska',
    code: 'AK',
  },
  {
    label: 'Arizona',
    code: 'AZ',
  },
  {
    label: 'Arkansas',
    code: 'AR',
  },
  {
    label: 'California',
    code: 'CA',
  },
  {
    label: 'Colorado',
    code: 'CO',
  },
  {
    label: 'Connecticut',
    code: 'CT',
  },
  {
    label: 'Delaware',
    code: 'DE',
  },
  {
    label: 'Florida',
    code: 'FL',
  },
  {
    label: 'Georgia',
    code: 'GA',
  },
  {
    label: 'Hawaii',
    code: 'HI',
  },
  {
    label: 'Idaho',
    code: 'ID',
  },
  {
    label: 'Illinois',
    code: 'IL',
  },
  {
    label: 'Indiana',
    code: 'IN',
  },
  {
    label: 'Iowa',
    code: 'IA',
  },
  {
    label: 'Kansas',
    code: 'KS',
  },
  {
    label: 'Kentucky',
    code: 'KY',
  },
  {
    label: 'Louisiana',
    code: 'LA',
  },
  {
    label: 'Maine',
    code: 'ME',
  },
  {
    label: 'Maryland',
    code: 'MD',
  },
  {
    label: 'Massachusetts',
    code: 'MA',
  },
  {
    label: 'Michigan',
    code: 'MI',
  },
  {
    label: 'Minnesota',
    code: 'MN',
  },
  {
    label: 'Mississippi',
    code: 'MS',
  },
  {
    label: 'Missouri',
    code: 'MO',
  },
  {
    label: 'Montana',
    code: 'MT',
  },
  {
    label: 'Nebraska',
    code: 'NE',
  },
  {
    label: 'Nevada',
    code: 'NV',
  },
  {
    label: 'New Hampshire',
    code: 'NH',
  },
  {
    label: 'New Jersey',
    code: 'NJ',
  },
  {
    label: 'New Mexico',
    code: 'NM',
  },
  {
    label: 'New York',
    code: 'NY',
  },
  {
    label: 'North Carolina',
    code: 'NC',
  },
  {
    label: 'North Dakota',
    code: 'ND',
  },
  {
    label: 'Ohio',
    code: 'OH',
  },
  {
    label: 'Oklahoma',
    code: 'OK',
  },
  {
    label: 'Oregon',
    code: 'OR',
  },
  {
    label: 'Pennsylvania',
    code: 'PA',
  },
  {
    label: 'Rhode Island',
    code: 'RI',
  },
  {
    label: 'South Carolina',
    code: 'SC',
  },
  {
    label: 'South Dakota',
    code: 'SD',
  },
  {
    label: 'Tennessee',
    code: 'TN',
  },
  {
    label: 'Texas',
    code: 'TX',
  },
  {
    label: 'Utah',
    code: 'UT',
  },
  {
    label: 'Vermont',
    code: 'VT',
  },
  {
    label: 'Virginia',
    code: 'VA',
  },
  {
    label: 'Washington',
    code: 'WA',
  },
  {
    label: 'West Virginia',
    code: 'WV',
  },
  {
    label: 'Wisconsin',
    code: 'WI',
  },
  {
    label: 'Wyoming',
    code: 'WY',
  },
  {
    label: 'District of Columbia',
    code: 'DC',
  },
  {
    label: 'American Samoa',
    code: 'AS',
  },
  {
    label: 'Guam',
    code: 'GU',
  },
  {
    label: 'Northern Mariana Islands',
    code: 'MP',
  },
  {
    label: 'Puerto Rico',
    code: 'PR',
  },
  {
    label: 'United States Minor Outlying Islands',
    code: 'UM',
  },
  {
    label: 'U.S. Virgin Islands',
    code: 'VI',
  },
];

export const WELLNESS_DOMAINS_LONG_DESCRIPTIONS: Record<THealthDomain, string> = {
  mental:
    'Mental Wholeness refers to how well an individual manages emotional well-being, psychological resilience, and mental clarity. This includes the ability to regulate emotions, cope with stress, manage anxiety or sadness, and seek support when needed. Strong Mental Wholeness supports focus, adaptability, and overall life satisfaction.',
  physical: `Physical Wholeness refers to how well an individual maintains physical well-being through self-care. This includes lifestyle habits such as sleep, exercise, substance use, diet, managing pain, and accessing medical care. It's foundational to other domains and essential for long-term health and vitality.`,
  social:
    'Social Wholeness refers to how well an individual builds and maintains meaningful relationships. This includes feeling close to others, family dynamics, connection to community, and support systems. Healthy social bonds contribute to a sense of belonging and emotional safety.',
  spiritual: `Spiritual Wholeness refers to how connected an individual feels to a deeper sense of meaning, purpose, or belief. This may include faith, spirituality, values alignment, or a sense of belonging to something greater than oneself. Spiritual Wholeness can provide hope, direction, and emotional grounding during life's challenges.`,
  vocational: `Vocational Wholeness refers to how stable, fulfilling, and sustainable an individual's work and financial situation feels. This includes job satisfaction, work-life balance, income sufficiency, financial stress, and the ability to plan for the future. It reflects whether your career and finances support or strain your overall well-being.`,
  purpose: `Purpose refers to an individual's sense of motivation, direction, and ability to recover from challenges. It includes personal growth, hope for the future, and confidence in facing adversity. A strong sense of purpose and resilience helps sustain momentum, especially during difficult times.`,
  fun: 'Fun Wholeness refers to how well an individual integrates joy, leisure, creativity, and rest into their life. It includes both spontaneous and intentional activities that bring play, laughter, and downtime. This domain supports emotional balance, creativity, and stress recovery.',
};

export const READINESS_DOMAIN_LONG_DESCRIPTION =
  'Readiness to Engage reflects how prepared and motivated an individual feels to take action in their well-being journey. It considers openness to change, confidence in the process, emotional willingness, and practical capacity. This domain influences how effectively someone can apply insights from the screener into meaningful growth.';
